import Button from '@doordash/component-button'
import { Inset, StackChildren, Text } from '@doordash/design-language'
import * as React from 'react'
import { connect } from 'react-redux'
import { i18nKeys } from 'translationTypes'
import { AusBackgroundCheckStep } from '.'
import * as events from '../../../../events'
import useHeightObserver from '../../../../hooks/useHeightObserver'
import i18n from '../../../../i18n'
import * as DA from '../../../../typeModules/events'
import Form from '../../../../utilities/Form'
import { formatErrorKeys } from '../../../../utilities/Form/utils'
import { addResizeObserverToElement } from '../../../../utilities/resizeObserver'
import { isMobile } from '../../../../utilities/userAgent'
import { validators as sharedValidators } from '../../../../utilities/validators'
import { BirthdateInputSplit, DateFormat } from '../../../atoms/birthdate-input'
import FullHeightWrapper from '../../../atoms/full-height-wrapper'
import SimpleSpacer from '../../../atoms/simple-spacer'
import StickyBottom from '../../../atoms/sticky-bottom'
import { VehicleTypeDropdown } from '../../../atoms/vehicle-type-dropdown'

const validators = {
  vehicleType: sharedValidators.vehicleType,
  birthday: sharedValidators.birthday,
}

interface VehicleDetailsProps {
  uniqueLink: string
  vehicleTypes: VehicleType[]
  onSubmit: (
    uniqueLink: string,
    data: Record<string, string>,
    backgroundCheckStep: AusBackgroundCheckStep
  ) => void
  trackBackgroundCheckSteps: (string) => Promise<void>
}

interface VehicleType {
  [key: number]: {
    name: string
    needs_license_info: boolean
  }
}

const VehicleDetails: React.FC<VehicleDetailsProps> = ({
  onSubmit,
  uniqueLink,
  vehicleTypes,
  trackBackgroundCheckSteps,
}) => {
  const {
    height: footerHeight,
    resizeObserver: footerResizeObserver,
  } = useHeightObserver()

  const fields = {
    vehicleType: -1,
    birthday: '',
  }

  const processVehicleTypes = vehicleTypes =>
    Object.keys(vehicleTypes).map(key => ({
      value: key,
      ...vehicleTypes[key],
    }))

  const handleSubmit = async data =>
    await onSubmit(uniqueLink, data, AusBackgroundCheckStep.VEHICLE_DETAILS)

  return (
    <FullHeightWrapper isMobile={isMobile}>
      <StackChildren size={StackChildren.Sizes.Large}>
        <Text tag="h1" styles={Text.Styles.Title1}>
          {i18n.t(i18nKeys.backgroundCheck__australiaPageTitle)}
        </Text>
        <Text
          tag="p"
          styles={Text.Styles.Body1}
          color={Text.Colors.TextSecondary}
        >
          {i18n.t(i18nKeys.backgroundCheck__vehicleExplanation)}
        </Text>
        <Form onSubmit={handleSubmit} validators={validators} fields={fields}>
          {(fields, handleChange, handleSubmit, { isLoading }) => (
            <form onSubmit={handleSubmit}>
              <StackChildren size={StackChildren.Sizes.Large}>
                <VehicleTypeDropdown
                  label={i18n.t(i18nKeys.backgroundCheck__vehicleTypeLabel)}
                  description={i18n.t(i18nKeys.common__required)}
                  vehicleTypes={processVehicleTypes(vehicleTypes)}
                  onChange={handleChange.bind(this, 'vehicleType')}
                  {...fields.vehicleType}
                />
                <BirthdateInputSplit
                  id="birthdate-input-split"
                  format={DateFormat.YEAR_MONTH_DAY}
                  label={i18n.t(i18nKeys.common__birthdate)}
                  onChange={handleChange.bind(this, 'birthday')}
                  birthdayField={fields.birthday}
                  {...fields.birthday}
                />
                {isMobile && <SimpleSpacer $height={footerHeight} />}
                <StickyBottom isMobile={isMobile}>
                  <Inset
                    ref={addResizeObserverToElement(footerResizeObserver)}
                    verticalSize={Inset.Sizes.Small}
                    horizontalSize={
                      isMobile ? Inset.Sizes.Small : Inset.Sizes.None
                    }
                  >
                    <Button
                      onClick={async () => {
                        trackBackgroundCheckSteps({
                          page: DA.BackgroundCheckPages.Vehicle,
                          action: DA.EventActions.Continue,
                        })
                        try {
                          await handleSubmit()
                        } catch (e) {
                          console.warn(e)
                          trackBackgroundCheckSteps({
                            page: DA.BackgroundCheckPages.Vehicle,
                            action: DA.EventActions.Error,
                            value: formatErrorKeys(e, fields, true),
                          })
                        }
                      }}
                      state={
                        isLoading
                          ? Button.States.Loading
                          : Button.States.Default
                      }
                    >
                      {i18n.t(i18nKeys.common__continue)}
                    </Button>
                  </Inset>
                </StickyBottom>
              </StackChildren>
            </form>
          )}
        </Form>
      </StackChildren>
    </FullHeightWrapper>
  )
}

const mapDispatchToProps = {
  trackBackgroundCheckSteps: events.trackBackgroundCheckSteps,
}

export default connect(null, mapDispatchToProps)(VehicleDetails)
